import { Box, Dropdown, Flex, Image, Typography } from "@qws-org/ui-kit";
import type { FC } from "react";
import React, { useState } from "react";

import type { GetSettingsQuery as GetSettingsQuerySdk } from "~/core/sdk";
import { changeLanguage } from "~/i18n";
import { ArrowDown, Globe } from "~/icons";
import { useDirectus } from "~/shared/utils/directus/useDirectus";

export type LocaleSelectProps = {
  languages: GetSettingsQuerySdk["languages"];
  availableLanguages: Array<string>;
  language: string;
  variant?: "static" | "positioned";
  offset?: number;
  isNonModal?: boolean;
};

function isLanguageCode(
  code: string | null | undefined,
  availableLanguages: Array<string>,
): code is LanguageCode {
  if (code) {
    return availableLanguages.includes(code);
  }
  return false;
}

export const LocaleSelect: FC<LocaleSelectProps> = ({
  languages,
  language,
  availableLanguages,
  variant = "positioned",
  offset,
  isNonModal = true,
}) => {
  const { getDirectusImage } = useDirectus();
  const [isActive, setIsActive] = useState(false);
  const selectedLang = languages?.find((item) => {
    if (item?.code === language) {
      return item;
    }

    return undefined;
  });

  return (
    <Dropdown.Trigger
      onOpenChange={(isOpen) => setIsActive(isOpen)}
      onAction={(key) => {
        const index = Number(key);

        if (
          Number.isInteger(index) &&
          index < languages.length &&
          languages[index].code !== language &&
          isLanguageCode(languages[index].code, availableLanguages)
        ) {
          changeLanguage(languages[index].code);
        }
      }}
      strategy="mixed"
      isNonModal={isNonModal}
      placement="top left"
      offset={offset}
      variant={variant}
      triggerElement={
        <Box
          cursor="pointer"
          data-testid="secondary-layout-locale-switch"
          bg={"surface-secondary-default"}
          border={{ radius: "button" }}
          minWidth={110}
        >
          <Flex
            align="center"
            justify="center"
            p={{ py: 8, px: 12 }}
            color="text-primary-default"
            hover={{ color: "text-primary-default" }}
            gap={4}
          >
            <Globe />
            <Typography
              fontSize="button.mobile.small"
              lineHeight="button.mobile.small"
              textTransform="uppercase"
              fontWeight="mobile.bold"
              md={{
                fontWeight: "desktop.bold",
                fontSize: "button.desktop.small",
                lineHeight: "button.desktop.small",
              }}
              p={{ pr: 8, pl: 4 }}
            >
              {selectedLang ? selectedLang?.code : "Pick"}
            </Typography>
            <ArrowDown
              className={`${isActive && "transform rotate-[-90deg]"}`}
            />
          </Flex>
        </Box>
      }
      dropdownWrapper={(wrapperProps) => (
        <Flex
          flexDirection="column"
          bg={"surface-secondary-default"}
          border={{ radius: "button" }}
          minWidth={110}
        >
          {wrapperProps.children}
        </Flex>
      )}
    >
      {languages?.map(
        (item, index) =>
          item && (
            <Dropdown.Item key={index} textValue={item?.code}>
              <Flex
                align="center"
                justify="flex-start"
                gap={8}
                key={index}
                p={{ py: 12, pl: 16 }}
                className="cursor-pointer"
                textTransform="uppercase"
                onClick={() => {
                  if (
                    languages[index].code !== language &&
                    isLanguageCode(languages[index].code, availableLanguages)
                  ) {
                    changeLanguage(languages[index].code);
                  }
                }}
              >
                {item?.icon?.id && (
                  <Image
                    value={getDirectusImage(item.icon.id ?? "", {
                      format: "webp",
                      width: 24,
                    })}
                  />
                )}
                <Typography
                  fontSize="button.mobile.small"
                  lineHeight="button.mobile.small"
                  textTransform="uppercase"
                  fontWeight="mobile.bold"
                  md={{
                    fontWeight: "desktop.bold",
                    fontSize: "button.desktop.small",
                    lineHeight: "button.desktop.small",
                  }}
                >
                  {item?.code}
                </Typography>
              </Flex>
            </Dropdown.Item>
          ),
      )}
    </Dropdown.Trigger>
  );
};
